import { Container, FillGradient, Graphics, Rectangle, Sprite, Text, type DestroyOptions, type Texture } from 'pixi.js';
import { COLORS, DESIGN_W, RADIUS, BORDER, textStyle, type TextOpts } from '../app/theme';
import { viewport } from '../app/viewport';
import { pulse } from '../app/anim';
import type { Atlas } from '../assets/AtlasLoader';
import { hapticImpact } from '../platform/telegram';
import { AccessibleButton } from './accessibility';
import { dismissKeyboard } from './keyboard';

/**
 * Module-level registry of standalone Unity UI icons (coin, return, …), set once at boot so ctx-less
 * helpers like `header()` can reach them. Shares the same Map instance as AppContext.icons.
 */
let uiIcons: Map<string, Texture> = new Map();
export function setUiIcons(map: Map<string, Texture>): void {
  uiIcons = map;
}
export function getUiIcon(name: string): Texture | undefined {
  return uiIcons.get(name);
}

/** Quick themed Text. Position via the returned object's .position. */
export function makeText(text: string, opts: TextOpts = {}): Text {
  return new Text({ text, style: textStyle(opts) });
}

/** Darken a packed RGB color by a multiplicative factor (1 = unchanged, 0 = black). */
function darken(color: number, f: number): number {
  const r = Math.round(((color >> 16) & 0xff) * f);
  const g = Math.round(((color >> 8) & 0xff) * f);
  const b = Math.round((color & 0xff) * f);
  return (r << 16) | (g << 8) | b;
}

/** Lighten a packed RGB color by a multiplicative factor (1 = unchanged), clamped to 255. */
function lighten(color: number, f: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * f));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * f));
  const b = Math.min(255, Math.round((color & 0xff) * f));
  return (r << 16) | (g << 8) | b;
}

export interface ButtonOpts {
  width?: number;
  height?: number;
  fill?: number;
  textColor?: number;
  fontSize?: number;
  radius?: number;
  /** Optional leading icon, pinned to the RIGHT edge (RTL); label centers in the remaining width. */
  icon?: Texture | null;
  /** Spoken label when it needs to be more descriptive than the visible text. */
  accessibleLabel?: string;
  /** Disable the default light Telegram haptic for frequently repeated/inert controls. */
  haptic?: false;
}

/** A themed, tappable rounded button (hand-rolled for full theme control). */
export class Button extends Container {
  /** Inner wrapper so the press-scale shrinks from the button's center without shifting layout. */
  private readonly content = new Container();
  private readonly bg = new Graphics();
  private readonly labelText: Text;
  private readonly w: number;
  private readonly h: number;
  private readonly r: number;
  private fillColor: number;
  private readonly explicitTextColor?: number;
  private enabled = true;
  private goldGrad?: FillGradient;
  private hoverGrad?: FillGradient;
  private readonly a11yControl: AccessibleButton;
  private readonly onTap: () => void;

  constructor(text: string, onTap: () => void, opts: ButtonOpts = {}) {
    super();
    this.w = opts.width ?? 300;
    this.h = opts.height ?? 72;
    this.r = opts.radius ?? RADIUS.button;
    this.fillColor = opts.fill ?? COLORS.accent;
    this.explicitTextColor = opts.textColor;
    this.onTap = onTap;

    this.labelText = makeText(text, {
      size: opts.fontSize ?? 26,
      weight: '700',
      align: 'center',
    });
    this.labelText.anchor.set(0.5);
    // RTL icon zone reserved on the right edge (mirrors InfoPill); 0 when no icon.
    const iconSize = Math.round(this.h * 0.56);
    const iconW = opts.icon ? iconSize + 10 : 0;
    this.labelText.position.set((this.w - iconW) / 2, this.h / 2);
    // Gold buttons use dark-brown text (real game); colored/dark fills keep white text + a shadow.
    this.restyleLabel();

    this.content.addChild(this.bg, this.labelText);
    if (opts.icon) {
      const icon = iconSprite(opts.icon, iconSize);
      icon.position.set(this.w - iconSize - 6, (this.h - iconSize) / 2);
      this.content.addChild(icon);
    }
    this.content.pivot.set(this.w / 2, this.h / 2);
    this.content.position.set(this.w / 2, this.h / 2);
    this.addChild(this.content);
    this.redraw();

    const activate = (): void => {
      if (!this.enabled) return;
      dismissKeyboard();
      if (opts.haptic !== false) hapticImpact('light');
      pulse(this.content, 1.06, 180);
      this.onTap();
    };
    this.a11yControl = new AccessibleButton(this, this.w, this.h, opts.accessibleLabel ?? text, activate);

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', activate);
    this.on('pointerover', () => {
      if (this.enabled) this.redraw(true);
    });
    this.on('pointerout', () => {
      if (this.enabled) this.redraw(false);
    });
    this.on('pointerdown', () => {
      if (this.enabled) this.content.scale.set(0.97);
    });
    this.on('pointerup', () => {
      if (this.enabled) this.content.scale.set(1);
    });
    this.on('pointerupoutside', () => {
      if (this.enabled) this.content.scale.set(1);
    });
  }

  /** The gold body gradient (light gold top → accentDark bottom); the hover variant is darkened. Cached. */
  private gradient(hover: boolean): FillGradient {
    if (hover) return (this.hoverGrad ??= this.buildGradient(0.82));
    return (this.goldGrad ??= this.buildGradient(1));
  }

  private buildGradient(f: number): FillGradient {
    return new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: darken(lighten(COLORS.accent, 1.12), f) },
        { offset: 1, color: darken(COLORS.accentDark, f) },
      ],
    });
  }

  private redraw(hover = false): void {
    const { w, h, r } = this;
    this.bg.clear();
    // Soft drawn shadow (cheaper than a filter) reads as depth beneath the button.
    this.bg.roundRect(2, 4, w, h, r).fill({ color: COLORS.scrim, alpha: 0.28 });
    // Gold buttons get a premium vertical gradient; other fills stay flat (darken on hover).
    if (this.fillColor === COLORS.accent) {
      this.bg.roundRect(0, 0, w, h, r).fill(this.gradient(hover));
    } else {
      this.bg.roundRect(0, 0, w, h, r).fill({ color: hover ? darken(this.fillColor, 0.82) : this.fillColor });
    }
    // Inner top gloss (same technique as the info pills) + a darker carved bevel edge.
    this.bg.roundRect(3, 3, w - 6, h * 0.42, r * 0.7).fill({ color: COLORS.white, alpha: 0.14 });
    this.bg.roundRect(0, 0, w, h, r).stroke({ width: 1, color: darken(this.fillColor, 0.55), alpha: 0.9 });
  }

  /** Pick the label color + shadow for the current fill: dark-on-gold, white-on-everything-else. */
  private restyleLabel(): void {
    const isGold = this.fillColor === COLORS.accent;
    this.labelText.style.fill = this.explicitTextColor ?? (isGold ? COLORS.buttonTextDark : COLORS.accentText);
    this.labelText.style.dropShadow = isGold
      ? false
      : { color: 0x000000, alpha: 0.55, blur: 2, distance: 2, angle: Math.PI / 2 };
  }

  setText(text: string): this {
    this.labelText.text = text;
    this.a11yControl.setLabel(text);
    return this;
  }

  setEnabled(value: boolean): this {
    this.enabled = value;
    this.alpha = value ? 1 : 0.45;
    this.cursor = value ? 'pointer' : 'default';
    this.eventMode = value ? 'static' : 'none';
    this.a11yControl.setDisabled(!value);
    return this;
  }

  setFill(color: number): this {
    this.fillColor = color;
    this.restyleLabel();
    this.redraw();
    return this;
  }

  override destroy(options?: DestroyOptions): void {
    this.a11yControl.destroy(options);
    super.destroy(options);
  }
}

/**
 * The bottom-of-page «بازگشت» button used on every lobby page (replaces the old top-corner return
 * icon). Styled as a SECONDARY/dark button (panel fill + cream label) so it reads as distinct from
 * the gold primary action above it. Callers place it at the bottom, conventionally `(40, bottomBackY())`.
 */
export function backButton(onBack: () => void, label = 'بازگشت'): Button {
  return new Button(label, onBack, {
    width: DESIGN_W - 80,
    height: 80,
    fontSize: 28,
    fill: COLORS.panel,
    textColor: COLORS.text,
  });
}

/**
 * A translucent wood-glass card — the same 4-layer treatment as the Login/Register panels: a soft
 * drop-shadow, a frosted `panelSoft` body, a top sheen, and a thin gold hairline border. Use it to
 * group a centered block of form controls into a visible "container". Returns a positioned Container
 * (add it BEFORE the controls so it sits behind them).
 */
export function glassCard(x: number, y: number, w: number, h: number, radius = 28): Container {
  const c = new Container();
  c.addChild(
    new Graphics().roundRect(x + 3, y + 6, w, h, radius).fill({ color: COLORS.scrim, alpha: 0.32 }),
    new Graphics().roundRect(x, y, w, h, radius).fill({ color: COLORS.panelSoft, alpha: 0.28 }),
    new Graphics().roundRect(x + 2, y + 2, w - 4, h * 0.32, radius - 2).fill({ color: COLORS.white, alpha: 0.035 }),
    new Graphics().roundRect(x, y, w, h, radius).stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.55 }),
  );
  return c;
}

export interface PanelOpts {
  fill?: number;
  alpha?: number;
  radius?: number;
  border?: boolean;
  /** Draw a soft offset shadow behind the panel (for big strips / modal cards; off for flat list rows). */
  shadow?: boolean;
}

/** A rounded panel with a carved-wood frame (dark outer edge + thin gold inner hairline) and a subtle top sheen. */
export class Panel extends Container {
  constructor(width: number, height: number, opts: PanelOpts = {}) {
    super();
    const r = opts.radius ?? RADIUS.panel;
    const g = new Graphics();
    if (opts.shadow) {
      g.roundRect(0, 6, width, height, r).fill({ color: COLORS.scrim, alpha: 0.3 });
    }
    // Translucent body so the wood backdrop reads through, then a faint top-lit sheen overlay for depth.
    g.roundRect(0, 0, width, height, r).fill({
      color: opts.fill ?? COLORS.panel,
      alpha: opts.alpha ?? 0.85,
    });
    g.roundRect(0, 0, width, height, r).fill(
      new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 1 },
        colorStops: [
          { offset: 0, color: 'rgba(255,255,255,0.06)' },
          { offset: 0.5, color: 'rgba(255,255,255,0)' },
        ],
      }),
    );
    if (opts.border !== false) {
      // Carved frame: a dark recessed outer edge + a thin muted-gold inner hairline.
      g.roundRect(0, 0, width, height, r).stroke({ width: BORDER.base, color: darken(COLORS.panelBorder, 0.5), alpha: 0.9 });
      g.roundRect(1, 1, width - 2, height - 2, r - 1).stroke({ width: 1, color: COLORS.panelBorder, alpha: 0.5 });
    }
    this.addChild(g);
  }
}

export interface InfoPillOpts {
  width?: number;
  height?: number;
  fontSize?: number;
  icon?: Texture | null;
  textColor?: number;
  glow?: boolean;
}

/**
 * A black glossy "info pill" with a soft white glow — the real game's chrome for username, balance,
 * online count, and section headers. Auto-fits its width to the text unless `width` is given; the
 * body's top-left is the pill origin (the glow overhangs symmetrically). Call `setText()` to update
 * live (e.g. balance) — it re-fits and re-draws. Read `pillWidth` to center it precisely.
 */
export class InfoPill extends Container {
  private readonly glow = new Graphics();
  private readonly body = new Graphics();
  private readonly gloss = new Graphics();
  private readonly labelNode: Text;
  private readonly iconNode?: Container;
  private readonly h: number;
  private readonly fixedW?: number;
  private readonly withGlow: boolean;
  private readonly iconW: number;
  private readonly baseFont: number;
  private lastW = 0;

  constructor(text: string, opts: InfoPillOpts = {}) {
    super();
    this.h = opts.height ?? 56;
    this.fixedW = opts.width;
    this.withGlow = opts.glow !== false;
    this.baseFont = opts.fontSize ?? 24;
    this.labelNode = makeText(text, {
      size: this.baseFont,
      weight: '700',
      align: 'center',
      fill: opts.textColor ?? COLORS.white,
    });
    this.labelNode.anchor.set(0.5);
    const iconSize = Math.round(this.h * 0.6);
    this.iconW = opts.icon ? iconSize + 10 : 0;
    this.addChild(this.glow, this.body, this.gloss, this.labelNode);
    if (opts.icon) {
      this.iconNode = iconSprite(opts.icon, iconSize);
      this.addChild(this.iconNode);
    }
    this.redraw();
  }

  private redraw(): void {
    const pad = 28;
    // Fixed-width pills shrink their font (down to 14) so long Persian text never overflows the body
    // or runs into the reserved icon zone, instead of clipping. Auto-width pills just fit the text.
    if (this.fixedW) this.fitLabel(this.fixedW - this.iconW - pad * 2);
    const w = this.fixedW ?? Math.ceil(this.labelNode.width + pad * 2 + this.iconW);
    this.lastW = w;
    const h = this.h;
    const r = h / 2;

    this.glow.clear();
    if (this.withGlow) {
      this.glow
        .roundRect(-9, -9, w + 18, h + 18, r + 9)
        .fill({ color: COLORS.white, alpha: 0.06 })
        .roundRect(-4, -4, w + 8, h + 8, r + 4)
        .fill({ color: COLORS.white, alpha: 0.12 });
    }

    this.body.clear();
    this.body
      .roundRect(0, 0, w, h, r)
      .fill({ color: COLORS.pillBlack, alpha: 0.92 })
      .stroke({ width: BORDER.thin, color: COLORS.white, alpha: 0.5 });

    this.gloss.clear();
    this.gloss.roundRect(5, 4, w - 10, h * 0.4, r * 0.7).fill({ color: COLORS.white, alpha: 0.06 });
    // A faint gold hairline before the icon zone marks the "value" side (balance/online pills).
    if (this.iconNode) {
      this.gloss.roundRect(w - this.iconW + 2, h * 0.22, 1.5, h * 0.56, 0.75).fill({ color: COLORS.accent, alpha: 0.35 });
    }

    // RTL: label centered in the area left of the icon zone; icon (if any) on the right edge.
    this.labelNode.position.set((w - this.iconW) / 2, h / 2);
    if (this.iconNode) {
      const iconSize = Math.round(h * 0.6);
      this.iconNode.position.set(w - iconSize - 5, (h - iconSize) / 2);
    }
  }

  /** Shrink the label's font (to a floor of 14) until it fits the available inner width. */
  private fitLabel(avail: number): void {
    let fs = this.baseFont;
    this.labelNode.style.fontSize = fs;
    while (fs > 14 && this.labelNode.width > avail) {
      fs -= 1;
      this.labelNode.style.fontSize = fs;
    }
  }

  /** The drawn pill width (excludes the glow overhang) — use to center the pill on a target x. */
  get pillWidth(): number {
    return this.lastW;
  }

  setText(text: string): void {
    this.labelNode.text = text;
    this.redraw();
  }
}

/** Factory for an {@link InfoPill}. */
export function infoPill(text: string, opts: InfoPillOpts = {}): InfoPill {
  return new InfoPill(text, opts);
}

/**
 * The Unity wood backdrop, registered once at boot (main.ts). Module-level so `sceneBackground()`
 * stays a no-arg call from every scene. Null until loaded / if the image fails.
 */
let bgTexture: Texture | null = null;
export function setBackgroundTexture(tex: Texture | null): void {
  bgTexture = tex;
}

/**
 * Full-viewport background: the wood image (cover-fit + a slight dim), or a gradient fallback. Sized
 * to the **live** `viewport.h` (not the fixed 1280) so the wood covers the full screen on tall
 * phones — rebuilt per scene, so a resize re-flow picks up the new height.
 */
export function sceneBackground(): Container {
  const H = viewport.h;
  const c = new Container();
  if (bgTexture) {
    const s = new Sprite(bgTexture);
    const scale = Math.max(DESIGN_W / bgTexture.width, H / bgTexture.height);
    s.scale.set(scale);
    s.anchor.set(0.5);
    s.position.set(DESIGN_W / 2, H / 2);
    // Spotlight: a warm bloom in the upper-center over a radial vignette that darkens to the edges
    // (the real game's lit-table look), instead of a flat uniform dim.
    const bloom = new Graphics()
      .ellipse(DESIGN_W / 2, H * 0.34, DESIGN_W * 0.72, H * 0.42)
      .fill({ color: 0xfff2d0, alpha: 0.1 });
    const vignette = new Graphics().rect(0, 0, DESIGN_W, H).fill(
      new FillGradient({
        type: 'radial',
        center: { x: 0.5, y: 0.36 },
        innerRadius: 0,
        outerCenter: { x: 0.5, y: 0.36 },
        outerRadius: 0.78,
        // Warm, deep-wood edge (bgBottom-tinted) rather than a flat gray-black, for a richer vignette.
        colorStops: [
          { offset: 0, color: 'rgba(0,0,0,0)' },
          { offset: 0.55, color: 'rgba(20,10,4,0.14)' },
          { offset: 1, color: 'rgba(20,10,4,0.62)' },
        ],
      }),
    );
    c.addChild(s, bloom, vignette);
    return c;
  }
  // Fallback: the original layered gradient (wood tones now).
  const base = new Graphics().rect(0, 0, DESIGN_W, H).fill({ color: COLORS.bgBottom });
  const mid = new Graphics().rect(0, 0, DESIGN_W, H * 0.6).fill({ color: COLORS.bgMid, alpha: 0.55 });
  const glow = new Graphics()
    .ellipse(DESIGN_W / 2, 0, DESIGN_W * 0.9, H * 0.4)
    .fill({ color: COLORS.bgTop, alpha: 0.65 });
  c.addChild(base, mid, glow);
  return c;
}

/** A fixed-size container holding an atlas frame / standalone texture, centered and fit to `size`. */
export function iconSprite(tex: Texture | null | undefined, size: number): Container {
  const c = new Container();
  if (tex) {
    const s = new Sprite(tex);
    const scale = size / Math.max(tex.width, tex.height);
    s.scale.set(scale);
    s.anchor.set(0.5);
    s.position.set(size / 2, size / 2);
    c.addChild(s);
  }
  return c;
}

/** A square, icon-only tappable button (used for back/refresh in headers). */
export function iconButton(tex: Texture | null | undefined, size: number, onTap: () => void): Container {
  const c = iconSprite(tex, size);
  c.hitArea = new Rectangle(0, 0, size, size);
  c.eventMode = 'static';
  c.cursor = 'pointer';
  c.on('pointertap', onTap);
  c.on('pointerover', () => (c.alpha = 0.75));
  c.on('pointerout', () => (c.alpha = 1));
  return c;
}

/** A circular avatar from the profile atlas (avatar_sp_<index>), fit into a colored bubble. */
export function avatarSprite(atlas: Atlas | null, index: number, size: number): Container {
  const c = new Container();
  const bubble = new Graphics()
    .circle(size / 2, size / 2, size / 2)
    .fill({ color: COLORS.panelSoft })
    .stroke({ width: BORDER.base, color: COLORS.panelBorder });
  c.addChild(bubble);
  const tex = atlas?.frames.get(`avatar_sp_${index}`);
  if (tex) {
    const s = new Sprite(tex);
    const scale = size / Math.max(tex.width, tex.height);
    s.scale.set(scale);
    s.anchor.set(0.5);
    s.position.set(size / 2, size / 2);
    // Clip to the bubble.
    const mask = new Graphics().circle(size / 2, size / 2, size / 2).fill({ color: 0xffffff });
    c.addChild(mask, s);
    s.mask = mask;
  }
  return c;
}

/** A simple labeled on/off toggle (checkbox). */
export class Toggle extends Container {
  private value: boolean;
  private readonly box = new Graphics();

  constructor(label: string, initial: boolean, private readonly onChange: (v: boolean) => void) {
    super();
    this.value = initial;
    const size = 52;
    this.addChild(this.box);
    const txt = makeText(label, { size: 26, fill: COLORS.textDim });
    txt.anchor.set(0, 0.5);
    txt.position.set(size + 12, size / 2);
    this.addChild(txt);
    this.redraw(size);

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Rectangle(-8, -8, Math.max(360, txt.width + size + 28), 68);
    this.on('pointertap', () => {
      this.value = !this.value;
      hapticImpact('light');
      this.redraw(size);
      this.onChange(this.value);
    });
  }

  private redraw(size: number): void {
    this.box.clear();
    this.box.roundRect(0, 0, size, size, RADIUS.sm).fill({
      color: this.value ? COLORS.accent : COLORS.inputBg,
    });
    this.box.stroke({ width: BORDER.base, color: COLORS.panelBorder });
    if (this.value) {
      this.box
        .moveTo(size * 0.25, size * 0.52)
        .lineTo(size * 0.43, size * 0.72)
        .lineTo(size * 0.78, size * 0.28)
        .stroke({ width: 4, color: COLORS.accentText });
    }
  }

  get checked(): boolean {
    return this.value;
  }
}
